import { useState } from 'react'
import { useLocation, useNavigate, type Location } from 'react-router-dom'

import { useApiMutation } from '@/api/hooks/useApiMutation'
import { DEMO_PASSWORD, DEMO_USERNAME } from '@/api/mock/server'
import { Button } from '@/shared/ui/Button'
import { Field, TextInput } from '@/shared/ui/Field'

import { useAuth } from './useAuth'

const IS_MOCK = ((import.meta.env.VITE_API_MODE as string | undefined) ?? 'mock') === 'mock'

interface RedirectState {
  from?: Location
}

/**
 * Pagina di login.
 *
 * Fuori dal telaio della dashboard: nessuna barra, nessuna navigazione, così
 * chi non è ancora dentro non vede una struttura vuota intorno a un form.
 */
export default function LoginView() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const mutation = useApiMutation({
    mutationFn: (input: { username: string; password: string }) => login(input),
    onSuccess: () => {
      const state = location.state as RedirectState | null
      void navigate(state?.from?.pathname ?? '/', { replace: true })
    },
  })

  return (
    <div className="bg-ink-900 flex min-h-dvh items-center justify-center p-6">
      <div className="bg-ink-800 w-full max-w-sm rounded-lg border border-[var(--hairline)] p-6">
        <p className="type-eyebrow mb-2">Dashboard</p>
        <h1 className="type-display mb-6">Accedi</h1>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            mutation.mutate({ username, password })
          }}
        >
          <Field label="Utente">
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                type="text"
                autoComplete="username"
                required
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            )}
          </Field>

          <Field label="Password">
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>

          <Button
            type="submit"
            variant="primary"
            pending={mutation.isPending}
            pendingLabel="Accesso…"
            className="mt-2"
          >
            Accedi
          </Button>
        </form>

        {IS_MOCK ? (
          <p className="text-thread-500 text-meta mt-4">
            Credenziale demo: {DEMO_USERNAME} / {DEMO_PASSWORD}
          </p>
        ) : null}
      </div>
    </div>
  )
}
